import React, { useState, useEffect } from 'react';
import { FaChartLine, FaArrowUp, FaArrowDown, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import API_URL from '../config';
import DrillDown from '../DrillDown';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

function Economy() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('gdp');
  const [drillDown, setDrillDown] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/economy`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success') {
          setData(result.data.sort((a, b) => a.year - b.year));
        } else {
          setError(result.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to connect to API');
        setLoading(false);
      });
  }, []);

  const latest = data.length > 0 ? data[data.length - 1] : null;
  const previous = data.length > 1 ? data[data.length - 2] : null;

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <FaSpinner className="text-5xl text-green-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading economy data...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center bg-white p-8 rounded-lg shadow-md">
        <p className="text-red-600 text-xl font-semibold mb-2">Error loading data</p>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  const METRICS = [
    {
      title: "GDP Growth",
      value: `${latest?.gdp_growth}%`,
      change: previous ? `${(latest?.gdp_growth - previous?.gdp_growth).toFixed(1)}% vs last year` : null,
      isPositive: latest?.gdp_growth > 0,
      subtitle: `${latest?.year}`,
      metric: {
        key: 'gdp_growth',
        label: 'GDP Growth Rate',
        color: '#10B981',
        unit: '%',
        higherIsBetter: true,
        description: 'Annual percentage growth rate of GDP at market prices',
        info: 'GDP growth rate measures how fast the economy is growing. A rate above 5% is generally considered strong for a developing economy like Kenya.'
      }
    },
    {
      title: "Inflation Rate",
      value: `${latest?.inflation_rate}%`,
      change: previous ? `${(latest?.inflation_rate - previous?.inflation_rate).toFixed(1)}% vs last year` : null,
      isPositive: latest?.inflation_rate < previous?.inflation_rate,
      subtitle: `${latest?.year}`,
      metric: {
        key: 'inflation_rate',
        label: 'Inflation Rate',
        color: '#EF4444',
        unit: '%',
        higherIsBetter: false,
        description: 'Annual percentage change in consumer prices',
        info: 'Inflation measures the rate at which prices increase over time. Kenya target inflation rate is between 2.5% and 7.5%.'
      }
    },
    {
      title: "Unemployment",
      value: `${latest?.unemployment_rate}%`,
      change: previous ? `${(latest?.unemployment_rate - previous?.unemployment_rate).toFixed(1)}% vs last year` : null,
      isPositive: latest?.unemployment_rate < previous?.unemployment_rate,
      subtitle: `${latest?.year}`,
      metric: {
        key: 'unemployment_rate',
        label: 'Unemployment Rate',
        color: '#F59E0B',
        unit: '%',
        higherIsBetter: false,
        description: 'Percentage of the labor force that is jobless',
        info: 'Unemployment rate shows the percentage of people actively looking for work but unable to find employment.'
      }
    },
    {
      title: "GDP Nominal",
      value: `$${latest?.gdp_nominal}B`,
      change: previous ? `+${((latest?.gdp_nominal - previous?.gdp_nominal) / previous?.gdp_nominal * 100).toFixed(1)}%` : null,
      isPositive: true,
      subtitle: `${latest?.year} estimate`,
      metric: {
        key: 'gdp_nominal',
        label: 'GDP Nominal (Billions USD)',
        color: '#4F46E5',
        unit: 'B',
        higherIsBetter: true,
        description: 'Total value of goods and services produced in Kenya',
        info: 'Nominal GDP measures the total economic output of Kenya in current US dollars without adjusting for inflation.'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center mb-4">
              <FaChartLine className="text-4xl mr-4" />
              <div>
                <h1 className="text-4xl font-bold">Economic Indicators</h1>
                <p className="text-green-200 mt-1">Kenya GDP, Inflation & Employment Trends</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Key Metrics */}
        {latest && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {METRICS.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MetricCard
                  {...item}
                  onClick={() => setDrillDown(item.metric)}
                />
              </motion.div>
            ))}
          </div>
        )}

        {/* Chart Tabs */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Economic Trends</h3>
            <div className="flex gap-2">
              {[
                { key: 'gdp', label: 'GDP Growth' },
                { key: 'inflation', label: 'Inflation' },
                { key: 'unemployment', label: 'Unemployment' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveChart(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activeChart === tab.key
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* GDP Growth Area Chart */}
          {activeChart === 'gdp' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="gdpGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}%`, 'GDP Growth']}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="gdp_growth"
                    stroke="#10B981"
                    strokeWidth={3}
                    fill="url(#gdpGradient)"
                    dot={{ fill: '#10B981', r: 5 }}
                    activeDot={{ r: 8 }}
                    name="GDP Growth %"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Inflation Area Chart */}
          {activeChart === 'inflation' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="inflationGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}%`, 'Inflation Rate']}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="inflation_rate"
                    stroke="#EF4444"
                    strokeWidth={3}
                    fill="url(#inflationGradient)"
                    dot={{ fill: '#EF4444', r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Inflation Rate %"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Unemployment Bar Chart */}
          {activeChart === 'unemployment' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}%`, 'Unemployment']}
                  />
                  <Legend />
                  <Bar
                    dataKey="unemployment_rate"
                    fill="#F59E0B"
                    name="Unemployment %"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </div>

        {/* Historical Data Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Historical Data</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Year</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">GDP Growth %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">GDP (B)</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Inflation %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Unemployment %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Public Debt %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {[...data].reverse().map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border-t hover:bg-green-50 transition cursor-pointer"
                  >
                    <td className="px-4 py-3 font-semibold text-green-600">{row.year}</td>
                    <td className="px-4 py-3">
                      <span className={row.gdp_growth > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {row.gdp_growth}%
                      </span>
                    </td>
                    <td className="px-4 py-3">${row.gdp_nominal}B</td>
                    <td className="px-4 py-3">{row.inflation_rate}%</td>
                    <td className="px-4 py-3">{row.unemployment_rate}%</td>
                    <td className="px-4 py-3">{row.public_debt}%</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{row.source}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Data Source */}
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm text-gray-700">
            <strong>Data Sources:</strong> Kenya National Bureau of Statistics (KNBS),
            Central Bank of Kenya (CBK), World Bank
          </p>
        </div>
      </div>

      {/* DrillDown Panel */}
      <DrillDown
        isOpen={drillDown !== null}
        onClose={() => setDrillDown(null)}
        metric={drillDown}
        data={data}
      />
    </div>
  );
}

function MetricCard({ title, value, change, isPositive, subtitle, onClick }) {
  return (
    <div
      className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition cursor-pointer hover:scale-105 transform"
      onClick={onClick}
    >
      <p className="text-gray-600 text-sm mb-2">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
      <div className="flex items-center">
        {isPositive !== null && (
          isPositive ?
            <FaArrowUp className="text-green-600 mr-1" /> :
            <FaArrowDown className="text-red-600 mr-1" />
        )}
        <p className={`text-sm ${isPositive === true ? 'text-green-600' : isPositive === false ? 'text-red-600' : 'text-gray-600'}`}>
          {change}
        </p>
      </div>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
      <p className="text-xs text-indigo-500 mt-2 font-medium">Click for details →</p>
    </div>
  );
}

export default Economy;